<template>
  <BasePage :pageTitle="coachTitle" back-button show-menu show-footer>
    <LoadingSpinner v-if="isLoading" :text="__DICT__.loading" container-class="mt-3" />
    <template v-if="coachData">
      <!-- Bridge action: quick link to load this coach -->
      <div class="d-flex gap-2 mb-3">
        <button class="btn btn-primary btn-sm flex-fill" @click="handleLoadClick">
          <IconScan class="icon me-1" :size="16" />
          {{ __DICT__.actionMenu.loadCoach }}
        </button>
      </div>

      <!-- Direction Choice Modal -->
      <div class="modal modal-blur fade" :class="{ 'show': showDirectionModal }" tabindex="-1" style="display: block;" v-if="showDirectionModal">
        <div class="modal-dialog modal-sm modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ __DICT__.selectDirection }}</h5>
              <button type="button" class="btn-close" @click="showDirectionModal = false"></button>
            </div>
            <div class="modal-body d-grid gap-2">
              <button class="btn btn-primary" @click="navigateToLoad('outbound')">
                {{ __DICT__.outbound }}
              </button>
              <button class="btn btn-primary" @click="navigateToLoad('inbound')">
                {{ __DICT__.inbound }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade" :class="{ 'show': showDirectionModal }" v-if="showDirectionModal"></div>
      <!-- Coach Details Accordion (collapsed by default) -->
      <div class="card mb-3">

        <AccordionItem id="collapseCoachDetails" :title="__DICT__.coachDetails">
          <div class="row g-3">
            <!-- Left Column -->
            <div class="col-md-6">
              <!-- Operator Section -->
              <Card size="sm" margin="mb-3">
                <InfoRow :label="__DICT__.operator + ':'" :value="coachData.OPERATOR" />
                <div v-if="coachData.OPERATOR_NUMBER" class="text-end">
                  <a class="link-azure" @click="handlePhoneClick(coachData['OPERATOR_NUMBER'])">
                    {{ coachData.OPERATOR_NUMBER }}
                  </a>
                </div>
              </Card>

              <!-- Capacity Section -->
              <Card size="sm" margin="mb-3">
                <InfoRow :label="__DICT__.capacity + ':'" :value="coachData.Capacity" />
              </Card>

              <!-- Direction Tabs: Outbound / Inbound -->
              <div class="card card-sm mb-3">
                <div class="card-header">
                  <ul class="nav nav-tabs card-header-tabs">
                    <li class="nav-item">
                      <a href="#" class="nav-link" :class="{ 'active': activeTab === 'outbound' }"
                         @click.prevent="activeTab = 'outbound'">{{ __DICT__.outbound || 'Outbound' }}</a>
                    </li>
                    <li class="nav-item">
                      <a href="#" class="nav-link" :class="{ 'active': activeTab === 'inbound' }"
                         @click.prevent="activeTab = 'inbound'">{{ __DICT__.inbound || 'Inbound' }}</a>
                    </li>
                  </ul>
                </div>
                <div class="card-body">
                  <!-- Outbound Tab Content -->
                  <div v-if="activeTab === 'outbound'">
                    <div class="mb-3">
                      <InfoRow label="Spare Seats Outbound:" :value="calculateSpareOutbound()" />
                      <InfoRow
                        v-for="group in passengerGroupsOutbound"
                        :key="'ob-'+group.brand"
                        :label="'Outbound ' + group.brand + ':'"
                        :value="group.passengers.length"
                      />
                      <InfoRow
                        v-if="coachData['OB NUCO Staff'] !== '0'"
                        label="OB NUCO Staff:"
                        :value="coachData['OB NUCO Staff']"
                      />
                      <InfoRow
                        v-if="coachData['OB WLT Staff'] !== '0'"
                        label="OB Wasteland Staff:"
                        :value="coachData['OB WLT Staff']"
                      />
                      <template v-if="liveDataOutbound">
                        <InfoRow :label="__DICT__.liveLoaded + ':'" :value="`${liveDataOutbound.loaded} / ${liveDataOutbound.total}`" />
                      <!--  <InfoRow :label="__DICT__.liveSpareSeats + ':'" :value="parseInt(coachData.Capacity) - liveDataOutbound.total - (parseInt(coachData['OB NUCO Staff']) || 0) - (parseInt(coachData['OB WLT Staff']) || 0)" />-->
                      </template>
                    </div>

                    <!-- Outbound Route -->
                    <div>
                      <InfoRow :label="__DICT__.outboundRoute + ':'" :value="coachData['OB Route']" />
                      <InfoRow
                        label="DOCA:"
                        :value="coachData['DOCA Date'] + ' ' + coachData['DOCA Time']"
                        label-class="text-muted small"
                        value-class="text-muted small"
                        no-margin
                      />
                      <InfoRow
                        label="Ferry Booking Ref:"
                        :value="coachData['OB Booking Ref'] || 'Not provided'"
                        label-class="text-muted small"
                        value-class="text-muted small"
                        no-margin
                      />
                    </div>
                  </div>

                  <!-- Inbound Tab Content -->
                  <div v-else>
                    <div class="mb-3">
                      <InfoRow label="Spare Seats Inbound:" :value="calculateSpareInbound()" />
                      <InfoRow
                        v-for="group in passengerGroupsInbound"
                        :key="'ib-'+group.brand"
                        :label="'Inbound ' + group.brand + ':'"
                        :value="group.passengers.length"
                      />
                      <InfoRow
                        v-if="coachData['IB NUCO Staff'] !== '0'"
                        label="IB NUCO Staff:"
                        :value="coachData['IB NUCO Staff']"
                      />
                      <InfoRow
                        v-if="coachData['IB WLT Staff'] !== '0'"
                        label="IB Wasteland Staff:"
                        :value="coachData['IB WLT Staff']"
                      />
                      <template v-if="liveDataInbound">
                        <InfoRow :label="__DICT__.liveLoaded + ':'" :value="`${liveDataInbound.loaded} / ${liveDataInbound.total}`" />
                        <!--InfoRow :label="__DICT__.liveSpareSeats + ':'" :value="parseInt(coachData.Capacity) - liveDataInbound.total - (parseInt(coachData['IB NUCO Staff']) || 0) - (parseInt(coachData['IB WLT Staff']) || 0)" />-->
                      </template>
                    </div>

                    <!-- Inbound Route -->
                    <div>
                      <InfoRow :label="__DICT__.inboundRoute + ':'" :value="coachData['IB Route']" />
                      <InfoRow
                        label="CADO:"
                        :value="coachData['CADO Date'] + ' ' + coachData['CADO Time']"
                        label-class="text-muted small"
                        value-class="text-muted small"
                        no-margin
                      />
                      <InfoRow
                        label="Ferry Booking Ref:"
                        :value="coachData['IB Booking Ref'] || 'Not provided'"
                        label-class="text-muted small"
                        value-class="text-muted small"
                        no-margin
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Driver Information -->
              <Card size="sm" margin="mb-3">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <InfoRow v-if="coachData['FEEDER']" label="Feeder:">
                  {{ coachData['FEEDER'] || 'No Feeder' }} - {{ coachData['FCONTACT'] || 'N/A' }}
                </InfoRow>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <InfoRow label="Main Driver:">
                  {{ coachData['MAIN DRIVER'] }} -
                  <a class="link-azure" @click="handlePhoneClick(coachData['MCONTACT'])">
                    {{ coachData['MCONTACT'] || 'Not provided' }}
                  </a>
                </InfoRow>
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <InfoRow label="Secondary Driver:" no-margin>
                  {{ coachData['2nd DRIVER'] }} - {{ coachData['2CONTACT'] || 'Not provided' }}
                </InfoRow>
              </Card>
            </div>

            <!-- Right Column -->
            <div class="col-md-6">
              <!-- Breakfast Stop -->
              <Card v-if="coachData['Breakfast Stop']" size="sm" margin="mb-3">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <strong class="d-block mb-2">{{ __DICT__.breakfastStop }}:</strong>
                <div class="mb-2">{{ coachData['Breakfast Stop'] }}</div>
                <div class="text-muted small mb-2">
                  <InfoRow :label="__DICT__.arrival + ':'" :value="coachData['ETA Breakfast Stop']" no-margin />
                  <InfoRow :label="__DICT__.departure + ':'" :value="coachData['Depart Breakfast Stop']" no-margin />
                </div>
                <a v-if="coachData['Breakfast Stop URL']"
                   :href="coachData['Breakfast Stop URL']"
                   target="_blank"
                   class="btn btn-sm btn-outline-primary">
                  {{ __DICT__.viewLocation }}
                </a>
              </Card>

              <Card v-if="coachData['Supermarket']" size="sm" margin="mb-3">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <strong class="d-block mb-2">{{ __DICT__.supermarket }}:</strong>
                <div class="mb-2">{{ coachData['Supermarket'] }}</div>
                <div class="text-muted small mb-2">
                  <InfoRow :label="__DICT__.arrival + ':'" :value="coachData['ETA Supermarket']" no-margin />
                  <InfoRow :label="__DICT__.departure + ':'" :value="coachData['Depart Super M']" no-margin />
                </div>
                <a v-if="coachData['Supermarket URL']"
                   :href="coachData['Supermarket URL']"
                   target="_blank"
                   class="btn btn-sm btn-outline-primary">
                  {{ __DICT__.viewLocation }}
                </a>
              </Card>

              <!-- Driver Residence -->
              <Card v-if="coachData['Driver Residence']" size="sm" margin="mb-3">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <strong class="d-block mb-2">{{ __DICT__.driverResidence }}:</strong>
                <div class="mb-2">{{ coachData['Driver Residence'] }}</div>
                <a v-if="coachData['Driver Residence URL']"
                   :href="coachData['Driver Residence URL']"
                   target="_blank"
                   class="btn btn-sm btn-outline-primary">
                  {{ __DICT__.viewLocation }}
                </a>
              </Card>

              <!-- Parking -->
              <Card v-if="coachData['Parking']" size="sm" margin="mb-3">
                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
                <strong class="d-block mb-2">{{ __DICT__.parking }}:</strong>
                <div class="mb-2">{{ coachData['Parking'] }}</div>
                <a v-if="coachData['Parking URL']"
                   :href="coachData['Parking URL']"
                   target="_blank"
                   class="btn btn-sm btn-outline-primary">
                  {{ __DICT__.viewLocation }}
                </a>
              </Card>
            </div>
            <!-- end right column -->
          </div>
          <!-- end row -->

          <!-- Transport Notes -->
          <Card v-if="coachData['Transport to accommodation']" size="sm" margin="mt-3">
            <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
            <strong class="d-block mb-2">{{ __DICT__.transportNotes }}:</strong>
            <div class="text-muted">{{ coachData['Transport to accommodation'] }}</div>
          </Card>

        </AccordionItem>


        <!-- end accordion -->
      </div>
      <!-- end card -->

      <!-- Direction Tabs for Journey Details -->
      <div class="card mb-3">
        <div class="card-header">
          <ul class="nav nav-tabs card-header-tabs">
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ 'active': activeTab === 'outbound' }"
                 @click.prevent="activeTab = 'outbound'">{{ __DICT__.outbound || 'Outbound' }}</a>
            </li>
            <li class="nav-item">
              <a href="#" class="nav-link" :class="{ 'active': activeTab === 'inbound' }"
                 @click.prevent="activeTab = 'inbound'">{{ __DICT__.inbound || 'Inbound' }}</a>
            </li>
          </ul>
        </div>
        <div class="card-body p-0">
          <!-- Outbound Tab Content -->
          <div v-if="activeTab === 'outbound'">
            <DepartureSchedule
                :departures="transportInfoOutbound?.departures"
                :title="__DICT__.outboundJourney"
            />

            <!-- Outbound Drop Off Locations/Times -->
            <template v-if="dropOffDeparturesOutbound.length > 0">
              <DepartureSchedule
                  :departures="dropOffDeparturesOutbound"
                  :title="'Outbound Drop Off'"
              />
            </template>

            <Card v-if="outboundReps?.length > 0" size="sm" margin="mt-3 mb-3" title="Outbound Staff" show-status-bar>
              <template #actions>
                <Badge :text="outboundReps.length" />
              </template>
              <div class="list-group list-group-flush">
                <div  @click="linkToRep(rep)" v-for="rep in outboundReps" :key="rep['GBS ID'] || rep.Name" class="list-group-item d-flex justify-content-between">
                  {{ rep.Name }}<span class="small">{{rep['OB Pickup Point']}}</span>
                </div>
              </div>
            </Card>
            <PaxAllocation
                :title="__DICT__.passengers + ' Outbound'"
                :inhabitants="passengersOutbound"
                :capacity="parseInt(coachData.Capacity)"
                show-count
                showBrand
            ></PaxAllocation>


          </div>

          <!-- Inbound Tab Content -->
          <div v-else>
            <DepartureSchedule
                :departures="transportInfoInbound?.departures"
                :title="__DICT__.inboundJourney"
            />

            <!-- Inbound Drop Off Locations/Times -->
            <template v-if="dropOffDeparturesInbound.length > 0">
              <DepartureSchedule
                  :departures="dropOffDeparturesInbound"
                  :title="'Inbound Drop Off'"
              />
            </template>

            <Card v-if="inboundReps?.length > 0" size="sm" margin="mb-3" title="Inbound Staff" show-status-bar>
              <template #actions>
                <Badge :text="inboundReps.length" />
              </template>
              <div class="list-group list-group-flush">
                <div  @click="linkToRep(rep)" v-for="rep in inboundReps" :key="rep['GBS ID'] || rep.Name" class="list-group-item d-flex justify-content-between">
                  {{ rep.Name }}<span class="small">{{rep['IB Pickup Point']}}</span>
                </div>
              </div>
            </Card>

            <PaxAllocation
                :title="__DICT__.passengers + ' Inbound'"
                :inhabitants="passengersInbound"
                :capacity="parseInt(coachData.Capacity)"
                show-count
                showBrand
            ></PaxAllocation>


          </div>
        </div>
      </div>

    </template>

    <div v-else-if="!isLoading" class="alert alert-important alert-danger" role="alert">
      {{ __DICT__.coachNotFound }}
    </div>
    <PhoneNumberActionSheet
        :phone-number="selectedPhoneNumber"
        :is-visible="showActionSheet"
        @close="showActionSheet = false"
    />
  </BasePage>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {useWeekStore} from '@/stores/week.js'
import BasePage from "@/components/BasePage.vue"
import PaxAllocation from "@/components/PaxAllocation.vue"
import {api} from "@/services/api.js";
import PhoneNumberActionSheet from "@/components/PhoneNumberActionSheet.vue";
import DepartureSchedule from "@/components/DeparturesSchedule.vue";
import {AccordionItem, Badge, Card, InfoRow, LoadingSpinner} from "@/components/atomic";
import {IconScan} from "@tabler/icons-vue";

const route = useRoute()
const router = useRouter()
const weekStore = useWeekStore()
const isLoading = ref(true)
const weekData = ref([])
const coachData = ref(null)
const showActionSheet = ref(false)
const selectedPhoneNumber = ref('')
const repData = ref([])
const activeTab = ref('outbound')
const coachDirections = ref({ outbound: false, inbound: false })
const showDirectionModal = ref(false)
const liveDataOutbound = ref(null)
const liveDataInbound = ref(null)

const handleLoadClick = () => {
  const hasOutbound = coachDirections.value.outbound
  const hasInbound = coachDirections.value.inbound

  if (hasOutbound && hasInbound) {
    showDirectionModal.value = true
  } else if (hasOutbound) {
    navigateToLoad('outbound')
  } else if (hasInbound) {
    navigateToLoad('inbound')
  } else {
    router.push({ name: 'selectLoadCoach' })
  }
}

const navigateToLoad = (direction) => {
  showDirectionModal.value = false
  router.push(`/loadCoach/${route.params.coachId}/${direction}`)
}

const handlePhoneClick = (number) => {
  if (!number) return

  if (number.substr(0, 2) === "44") {
    selectedPhoneNumber.value = "+" + number
  } else {
    selectedPhoneNumber.value = number
  }

  showActionSheet.value = true
}

const coachTitle = computed(() =>
    coachData.value ? `${__DICT__.coach} ${coachData.value.COACH}` : __DICT__.coach
)

const passengersInbound = computed(() =>
    weekData.value.filter(passenger =>
        passenger.allocPlaces?.some(place =>
            place.transport?.transportType === "coach_travel" &&
            place.transport?.coach === route.params.coachId && place.transport?.type === 'transport-inbound'
        )
    )
)

const passengersOutbound = computed(() =>
    weekData.value.filter(passenger =>
        passenger.allocPlaces?.some(place =>
            place.transport?.transportType === "coach_travel" &&
            place.transport?.coach === route.params.coachId && place.transport?.type === 'transport-outbound'
        )
    )
)
const linkToRep = (rep) => {
  router.push(`/rep/${rep['GBS ID']}`)
}


const outboundReps = computed(() => {
  return (repData.value || []).filter(rep => rep["OB Transport"] === route.params.coachId)
})

const inboundReps = computed(() => {
  return (repData.value || []).filter(rep => rep["IB Transport"] === route.params.coachId)
})

const calculateSpareOutbound = () => {
  const nucoOB = parseInt(coachData.value['OB NUCO Staff']) || 0;
  const wltOB = parseInt(coachData.value['OB WLT Staff']) || 0;
  return parseInt(coachData.value.Capacity) - (liveDataOutbound.value?.tickets.length + nucoOB + wltOB)
}

const calculateSpareInbound = () => {
  const nucoIB = parseInt(coachData.value['IB NUCO Staff']) || 0;
  const wltIB = parseInt(coachData.value['IB WLT Staff']) || 0;
  return parseInt(coachData.value.Capacity) - (liveDataInbound.value?.tickets.length + nucoIB + wltIB)
}

const passengerGroupsInbound = computed(() => {
  // Ensure we have passengers to work with
  if (!passengersInbound.value?.length) return []

  // Create groups using reduce
  return Object.values(passengersInbound.value.reduce((groups, passenger) => {
    // Extract the brand code (part before hyphen)
    const brand = passenger.reference.split('-')[0]

    // If this brand doesn't exist in our groups yet, create it
    if (!groups[brand]) {
      groups[brand] = {
        brand,
        passengers: []
      }
    }

    // Add the passenger to their brand group
    groups[brand].passengers.push(passenger)

    return groups
  }, {}))
})

const passengerGroupsOutbound = computed(() => {
  const tickets = liveDataOutbound.value?.tickets
  if (!tickets?.length) return []

  return Object.values(tickets.reduce((groups, passenger) => {
    // Extract the brand code (part before hyphen)
    const brand = passenger.bookingRef.split('-')[0]

    // If this brand doesn't exist in our groups yet, create it
    if (!groups[brand]) {
      groups[brand] = {
        brand,
        passengers: []
      }
    }

    // Add the passenger to their brand group
    groups[brand].passengers.push(passenger)

    return groups
  }, {}))
})

const transportInfoInbound = computed(() => {
  const firstPassenger = passengersInbound.value[0]
  const transportInfo = firstPassenger?.allocPlaces?.find(
      place => place.transport?.coach === route.params.coachId && place.transport?.type === 'transport-inbound'
  )?.transport

  if (transportInfo?.departures) {
    return {
      ...transportInfo,
      departures: [...transportInfo.departures].sort((a, b) =>
          new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      )
    }
  }

  return transportInfo
})

const transportInfoOutbound = computed(() => {
  const firstPassenger = passengersOutbound.value[0]
  const transportInfo = firstPassenger?.allocPlaces?.find(
      place => place.transport?.coach === route.params.coachId && place.transport?.type === 'transport-outbound'
  )?.transport
  if (transportInfo?.departures) {
    return {
      ...transportInfo,
      departures: [...transportInfo.departures].sort((a, b) =>
          new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()
      )
    }
  }

  return transportInfo
})

// Build a list of unique Drop Off locations/times for Outbound
const dropOffDeparturesOutbound = computed(() => {
  if (!passengersOutbound.value || passengersOutbound.value.length === 0) return []

  const itemsMap = new Map()

  for (const passenger of passengersOutbound.value) {
    const place = passenger.allocPlaces?.find(p =>
        p.transport?.transportType === 'coach_travel' &&
        p.transport?.coach === route.params.coachId &&
        p.transport?.type === 'transport-outbound'
    )

    const loc = place?.transport?.dropOffLocation
    if (!loc) continue

    const timeKey = loc.dateTime || loc.loading_time || ''
    const key = `${loc.location || ''}|${timeKey}`
    if (!itemsMap.has(key)) itemsMap.set(key, loc)
  }

  const uniques = Array.from(itemsMap.values())
  uniques.sort((a, b) => {
    const at = new Date(a.dateTime || a.loading_time || 0).getTime()
    const bt = new Date(b.dateTime || b.loading_time || 0).getTime()
    return at - bt
  })
  return uniques
})

// Build a list of unique Drop Off locations/times for Inbound
const dropOffDeparturesInbound = computed(() => {
  if (!passengersInbound.value || passengersInbound.value.length === 0) return []

  const itemsMap = new Map()

  for (const passenger of passengersInbound.value) {
    const place = passenger.allocPlaces?.find(p =>
        p.transport?.transportType === 'coach_travel' &&
        p.transport?.coach === route.params.coachId &&
        p.transport?.type === 'transport-inbound'
    )

    const loc = place?.transport?.dropOffLocation
    if (!loc) continue

    const timeKey = loc.dateTime || loc.loading_time || ''
    const key = `${loc.location || ''}|${timeKey}`
    if (!itemsMap.has(key)) itemsMap.set(key, loc)
  }

  const uniques = Array.from(itemsMap.values())
  uniques.sort((a, b) => {
    const at = new Date(a.dateTime || a.loading_time || 0).getTime()
    const bt = new Date(b.dateTime || b.loading_time || 0).getTime()
    return at - bt
  })
  return uniques
})

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-GB')
}

function formatTime(dateString) {
  return new Date(dateString).toLocaleTimeString('en-GB')
}

onMounted(async () => {
  try {
    const [weekDataResult, repDataResult, coachDataResult, allCoachesResult] = await Promise.all([
      weekStore.weekData,
      weekStore.repData,
      api.get(`/api/coach/coachData/${weekStore.weekId}`),
      api.get(`/api/coach/allCoaches/${weekStore.weekId}`)
    ])

    weekData.value = weekDataResult
    repData.value = repDataResult
    coachData.value = coachDataResult.mergedData.find(
        coach => coach.COACH === route.params.coachId
    )

    coachDirections.value = {
      outbound: allCoachesResult.outbound?.includes(route.params.coachId) ?? false,
      inbound: allCoachesResult.inbound?.includes(route.params.coachId) ?? false,
    }
  } catch (error) {
    console.error('Error loading coach data:', error)
  } finally {
    isLoading.value = false
  }

  // Fetch live ticket data for both directions after page renders
  Promise.all([
    api.get(`/api/coach/allLiveBarcodes/${route.params.coachId}/direction/outbound/${weekStore.systemId}`).catch(() => null),
    api.get(`/api/coach/allLiveBarcodes/${route.params.coachId}/direction/inbound/${weekStore.systemId}`).catch(() => null),
  ]).then(([liveOb, liveIb]) => {
    liveDataOutbound.value = liveOb
    liveDataInbound.value = liveIb
  })
})
</script>
